import React, { useMemo, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import { scopeCss } from 'shared/lib/widgetCode/scopeCss';
import { scopeCssLegacyCustom } from 'shared/lib/widgetCode/legacyScopers';
import { wrapWidgetScriptV2 } from 'shared/lib/widgetScriptRuntime';
// The sandboxed preview has no storage of its own; the shim keeps localStorage and cookies working, as on the page.
import { escapeClosingTag, PREVIEW_STORAGE_SHIM } from '~/lib/previewSandbox';

const useStyles = createUseStyles({
  container: {
    width: '100%',
    height: '100%',
    background: '#ffffff',
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
  },
  empty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#94a3b8',
    fontSize: '14px',
    textAlign: 'center',
    padding: '40px',
  },
});

interface Props {
  html: string;
  css: string;
  js: string;
  props?: Record<string, unknown>;
  widgetName?: string;
  /** 2 = preview with the v2 scoper and script wrapper, like the live widget. */
  scoper?: number;
  /** False for proposals: the pasted code's script doesn't run. */
  runScripts?: boolean;
}

/**
 * Get a nested value from an object using dot notation
 * e.g., getNestedValue({ user: { name: 'John' } }, 'user.name') => 'John'
 */
const getNestedValue = (obj: Record<string, unknown>, path: string): unknown => {
  const keys = path.split('.');
  let value: unknown = obj;
  
  for (const key of keys) {
    if (value === null || value === undefined) {
      return undefined;
    }
    value = value[key];
  }
  
  return value;
};

/**
 * Replace {{.}} only at the top level (not inside nested {{#...}}...{{/...}} blocks)
 * This prevents premature replacement of {{.}} in nested arrays
 */
const replaceTopLevelDot = (template: string, item: unknown): string => {
  // Get the replacement value
  let replacement = '';
  if (typeof item !== 'object' || item === null) {
    replacement = String(item ?? '');
  } else {
    // Try common object value patterns
    const obj = item as Record<string, unknown>;
    if ('value' in obj) replacement = String(obj.value ?? '');
    else if ('text' in obj) replacement = String(obj.text ?? '');
    else if ('name' in obj) replacement = String(obj.name ?? '');
    else if ('label' in obj) replacement = String(obj.label ?? '');
    else {
      const keys = Object.keys(obj).filter(k => !k.startsWith('_'));
      if (keys.length === 1) replacement = String(obj[keys[0]] ?? '');
    }
  }
  
  // Parse through template, tracking nesting depth
  let depth = 0;
  let result = '';
  let i = 0;
  
  while (i < template.length) {
    // Check for {{# (opening array block)
    if (template.slice(i, i + 3) === '{{#') {
      depth++;
      result += template[i];
      i++;
    }
    // Check for {{/ (closing array block)
    else if (template.slice(i, i + 3) === '{{/') {
      depth--;
      result += template[i];
      i++;
    }
    // Check for {{.}} only at depth 0
    else if (depth === 0 && template.slice(i, i + 5) === '{{.}}') {
      result += replacement;
      i += 5; // Skip past {{.}}
    }
    // Check for {{ . }} with spaces only at depth 0
    else if (depth === 0 && template.slice(i).match(/^\{\{\s*\.\s*\}\}/)) {
      const match = template.slice(i).match(/^\{\{\s*\.\s*\}\}/)!;
      result += replacement;
      i += match[0].length;
    }
    else {
      result += template[i];
      i++;
    }
  }
  
  return result;
};

/**
 * Process array blocks in the template (supports nested arrays)
 * Syntax: {{#arrayName}}...content...{{/arrayName}}
 * Inside the block, use {{fieldName}} for item fields, {{.}} for primitives
 */
const processArrayBlocks = (html: string, context: Record<string, unknown>): string => {
  // Match {{#arrayName}}...{{/arrayName}} blocks (non-greedy, handles nesting)
  const arrayBlockRegex = /\{\{#([a-zA-Z_][a-zA-Z0-9_]*)\}\}([\s\S]*?)\{\{\/\1\}\}/g;
  
  let result = html;
  let hasMatch = true;
  
  // Keep processing until no more array blocks are found (handles nested arrays)
  while (hasMatch) {
    hasMatch = false;
    result = result.replace(arrayBlockRegex, (match, arrayKey, template) => {
      hasMatch = true;
      const array = context[arrayKey];
      
      // If not an array or empty, return empty string
      if (!Array.isArray(array) || array.length === 0) {
        return '';
      }
      
      // For each item in the array, interpolate the template
      return array.map((item, index) => {
        // Create a context that includes the item's fields plus special variables
        const itemContext = typeof item === 'object' && item !== null
          ? { ...item, _index: index, _first: index === 0, _last: index === array.length - 1 }
          : { _value: item, _index: index, _first: index === 0, _last: index === array.length - 1 };
        
        // Replace placeholders in the template
        let itemResult = template;
        
        // Handle {{.}} for primitive values (like string arrays)
        // IMPORTANT: Only replace {{.}} at top level, not inside nested {{#...}}...{{/...}} blocks
        itemResult = replaceTopLevelDot(itemResult, item);
        
        // Handle {{_index}}, {{_first}}, {{_last}}
        itemResult = itemResult.replace(/\{\{\s*_index\s*\}\}/g, String(index));
        itemResult = itemResult.replace(/\{\{\s*_first\s*\}\}/g, String(itemContext._first));
        itemResult = itemResult.replace(/\{\{\s*_last\s*\}\}/g, String(itemContext._last));
        
        // Handle {{fieldName}} for object properties (but not nested arrays yet)
        itemResult = itemResult.replace(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_.]*)\s*\}\}/g, (m, path) => {
          // Skip special variables already handled and array block markers
          if (path === '.' || path.startsWith('_') || path.startsWith('#') || path.startsWith('/')) return m;
          
          const value = getNestedValue(itemContext, path);
          
          if (value === null || value === undefined) {
            return '';
          } else if (Array.isArray(value)) {
            // Don't stringify arrays - they'll be processed in the next iteration
            return m;
          } else if (typeof value === 'object') {
            return JSON.stringify(value);
          } else {
            return String(value);
          }
        });
        
        // Recursively process nested array blocks with the item context
        if (typeof item === 'object' && item !== null) {
          itemResult = processArrayBlocks(itemResult, itemContext);
        }
        
        return itemResult;
      }).join('');
    });
  }
  
  return result;
};

/**
 * Replace prop placeholders in HTML with actual values
 * Supports {{propKey}} and {{nested.prop.key}} syntax
 * Also processes array blocks with {{#array}}...{{/array}}
 */
const interpolateProps = (html: string, props: Record<string, unknown>): string => {
  // First, process array blocks
  let result = processArrayBlocks(html, props);
  
  // Then, handle remaining simple interpolations
  result = result.replace(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_.]*)\s*\}\}/g, (match, path) => {
    const value = getNestedValue(props, path);
    
    if (value === null || value === undefined) {
      return '';
    } else if (typeof value === 'object') {
      // Don't serialize arrays/objects for simple interpolation
      if (Array.isArray(value)) return '';
      return JSON.stringify(value);
    } else {
      return String(value);
    }
  });
  
  return result;
};

const SCOPE_CLASS = 'widget-scope';

/**
 * Sanitize a string to be a valid CSS custom property name segment
 */
const sanitizeCssVarName = (name: string): string => {
  return name
    .replace(/[^a-zA-Z0-9_-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

/**
 * Convert props object to CSS custom properties (CSS variables)
 * Flattens nested objects with hyphen delimiter (e.g., colors.primary → --colors-primary)
 * Skips arrays and null/undefined values
 */
const propsToCssVariables = (
  props: Record<string, unknown>,
  prefix = ''
): Record<string, string> => {
  const vars: Record<string, string> = {};
  
  for (const [key, value] of Object.entries(props)) {
    // Skip internal props that start with underscore
    if (key.startsWith('_')) continue;
    
    // Sanitize key to be valid in CSS custom property name
    const sanitizedKey = sanitizeCssVarName(key);
    if (!sanitizedKey) continue;
    
    const varName = prefix ? `--${prefix}-${sanitizedKey}` : `--${sanitizedKey}`;
    
    if (value === null || value === undefined) {
      continue;
    } else if (Array.isArray(value)) {
      continue;
    } else if (typeof value === 'object') {
      const nestedPrefix = prefix ? `${prefix}-${sanitizedKey}` : sanitizedKey;
      Object.assign(vars, propsToCssVariables(value as Record<string, unknown>, nestedPrefix));
    } else if (typeof value === 'boolean') {
      vars[varName] = value ? 'true' : 'false';
    } else {
      vars[varName] = String(value);
    }
  }
  
  return vars;
};


const LivePreview: React.FC<Props> = ({ html, css, js, props = {}, widgetName = 'preview', scoper, runScripts = true }) => {
  const isV2 = scoper === 2;
  const c = useStyles();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Generate a unique scope class
  const scopeClass = useMemo(() => {
    const sanitizedName = widgetName.replace(/[^a-zA-Z0-9-_]/g, '-');
    return `${SCOPE_CLASS}-${sanitizedName}`;
  }, [widgetName]);

  // Interpolate props into HTML
  const interpolatedHtml = useMemo(() => {
    return interpolateProps(html, props);
  }, [html, props]);

  // Apply CSS scoping (v2 widgets use the same scoper as on the live page)
  const scopedCss = useMemo(() => {
    return isV2 ? scopeCss(css, scopeClass).css : scopeCssLegacyCustom(css, scopeClass);
  }, [css, scopeClass, isV2]);

  // Convert props to CSS variables for injection
  const cssVariables = useMemo(() => {
    return propsToCssVariables(props);
  }, [props]);

  // Generate the full HTML document for the iframe
  const srcDoc = useMemo(() => {
    // Script to set CSS variables on root element
    // This runs before user JS to ensure variables are available
    const cssVarsScript = `
      (function() {
        var cssVars = ${JSON.stringify(cssVariables)};
        var root = document.querySelector('.${scopeClass}');
        if (root) {
          Object.keys(cssVars).forEach(function(key) {
            root.style.setProperty(key, cssVars[key]);
          });
        }
      })();
    `;

    // Wrap JS in an IIFE with props and root element available
    // Use DOMContentLoaded to ensure DOM is ready
    const v2Js = isV2 && js.trim() ? wrapWidgetScriptV2({
      js,
      rootSelector: `.${scopeClass}`,
      label: 'Custom widget',
      variables: [
        '  var instanceId = "preview";',
        `  var instanceSelector = '.${scopeClass}';`,
        `  var __WIDGET_PROPS__ = ${JSON.stringify(props)};`
      ].join('\n')
    }) : '';
    const wrappedJs = !runScripts ? '' : isV2 ? v2Js : js.trim() ? `
      (function() {
        var scopeSelector = '.${scopeClass}';
        var __WIDGET_PROPS__ = ${JSON.stringify(props)};
        
        function executeScript() {
          var root = document.querySelector(scopeSelector);
          if (!root) {
            setTimeout(executeScript, 10);
            return;
          }
          try {
            ${js}
          } catch (error) {
            console.error('Widget script error:', error);
          }
        }
        
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', executeScript);
        } else {
          executeScript();
        }
      })();
    ` : '';

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  ${runScripts ? `<script>${PREVIEW_STORAGE_SHIM}</script>` : ''}
  <style>
    * {
      box-sizing: border-box;
    }
    body {
      margin: 0;
      padding: 16px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    }
    ${escapeClosingTag(scopedCss, 'style')}
  </style>
</head>
<body>
  <div class="${scopeClass}">
    <div>${interpolatedHtml}</div>
  </div>
  ${runScripts ? `<script>${cssVarsScript}</script>` : ''}
  ${wrappedJs ? `<script>${wrappedJs.replace(/<\/script/gi, '<\\/script')}</script>` : ''}
</body>
</html>
    `.trim();
  }, [interpolatedHtml, scopedCss, js, props, scopeClass, cssVariables, isV2, runScripts]);

  if (!html.trim() && !css.trim() && !js.trim()) {
    return (
      <div className={c.container}>
        <div className={c.empty}>
          <div>
            <p style={{ margin: '0 0 8px 0', fontWeight: 500 }}>Preview will appear here</p>
            <p style={{ margin: 0, fontSize: '13px' }}>
              Start writing HTML, CSS, or JavaScript to see your widget
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={c.container}>
      <iframe
        ref={iframeRef}
        className={c.iframe}
        srcDoc={srcDoc}
        title="Widget Preview"
        sandbox={runScripts ? 'allow-scripts' : ''}
      />
    </div>
  );
};

export default LivePreview;
